/* DoctorSoapRecorder — MVP UI for Whisper-to-SOAP doctor consultations.
 *
 * Flow:
 *  1. Click "Започни запис" → MediaRecorder captures audio
 *  2. Click "Спри" → upload to /api/doctor/soap/transcribe
 *  3. Backend → Whisper STT → GPT-5.2 → returns transcript + structured SOAP
 *  4. Doctor reviews/edits 4 SOAP fields
 *  5. Click "Запази" → POST /api/doctor/soap/save
 *
 * Bulgarian primary; English fallback via LanguageContext.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mic, Square, Loader2, Save, RotateCcw, FileText, Trash2, ChevronDown, ChevronUp,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "subjective", label_bg: "S — Субективно (Subjective)", label_en: "S — Subjective" },
  { key: "objective",  label_bg: "O — Обективно (Objective)",   label_en: "O — Objective" },
  { key: "assessment", label_bg: "A — Оценка (Assessment)",     label_en: "A — Assessment" },
  { key: "plan",       label_bg: "P — План (Plan)",             label_en: "P — Plan" },
];

export default function DoctorSoapRecorder() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [soap, setSoap] = useState({ subjective: "", objective: "", assessment: "", plan: "" });
  const [patientName, setPatientName] = useState("");
  const [savedNoteId, setSavedNoteId] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);

  // Load history once mounted
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/doctor/soap/list`);
        if (!cancelled) setHistory(data?.notes || []);
      } catch { /* ignore — first-time clinicians have no notes */ }
    })();
    return () => { cancelled = true; };
  }, [savedNoteId]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = rec;
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
      rec.onstop = handleRecordStopped;
      rec.start(1000);
      setRecording(true);
      setRecordSeconds(0);
      tickRef.current = setInterval(() => setRecordSeconds((s) => s + 1), 1000);
    } catch (e) {
      toast.error(bg ? "Няма достъп до микрофон" : "Microphone access denied");
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setRecording(false);
  };

  const handleRecordStopped = async () => {
    setProcessing(true);
    try {
      const blob = new Blob(chunksRef.current, { type: "audio/webm" });
      if (blob.size < 1000) {
        toast.error(bg ? "Записът е твърде кратък" : "Recording too short");
        setProcessing(false);
        return;
      }
      const fd = new FormData();
      fd.append("audio", blob, "consultation.webm");
      fd.append("language", language || "bg");
      const { data } = await axios.post(`${API}/doctor/soap/transcribe`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setTranscript(data?.transcript || "");
      setSoap({
        subjective: data?.soap?.subjective || "",
        objective: data?.soap?.objective || "",
        assessment: data?.soap?.assessment || "",
        plan: data?.soap?.plan || "",
      });
      setSavedNoteId(null);
      toast.success(bg ? "Транскрипцията е готова" : "Transcription ready");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail
          ? String(e.response.data.detail)
          : (bg ? "Грешка при транскрипция" : "Transcription failed"),
      );
    } finally {
      setProcessing(false);
    }
  };

  const saveNote = async () => {
    try {
      const payload = {
        soap: {
          ...soap,
          language: language || "bg",
          transcript,
          patient_name: patientName,
        },
        note_id: savedNoteId || null,
      };
      const { data } = await axios.post(`${API}/doctor/soap/save`, payload);
      setSavedNoteId(data.id || data.note_id || savedNoteId);
      toast.success(bg ? "Бележката е запазена" : "Note saved");
    } catch {
      toast.error(bg ? "Грешка при запазване" : "Save failed");
    }
  };

  const resetAll = () => {
    setTranscript("");
    setSoap({ subjective: "", objective: "", assessment: "", plan: "" });
    setPatientName("");
    setSavedNoteId(null);
    setRecordSeconds(0);
  };

  const loadFromHistory = (note) => {
    setSoap({
      subjective: note.subjective || "",
      objective: note.objective || "",
      assessment: note.assessment || "",
      plan: note.plan || "",
    });
    setTranscript(note.transcript || "");
    setPatientName(note.patient_name || "");
    setSavedNoteId(note.id);
    setHistoryOpen(false);
  };

  const deleteNote = async (id) => {
    try {
      await axios.delete(`${API}/doctor/soap/${id}`);
      setHistory((h) => h.filter((n) => n.id !== id));
      if (savedNoteId === id) resetAll();
    } catch { /* silent */ }
  };

  const cardCls = isDark
    ? "bg-[hsl(222,47%,9%)] border-white/5"
    : "bg-white border-gray-200";

  const inputCls = isDark
    ? "bg-[hsl(222,47%,12%)] border-white/10 text-white placeholder:text-gray-500"
    : "bg-white border-gray-200 text-gray-900 placeholder:text-gray-400";

  const minSec = `${String(Math.floor(recordSeconds / 60)).padStart(2, "0")}:${String(recordSeconds % 60).padStart(2, "0")}`;

  return (
    <div className={`rounded-2xl border p-5 space-y-4 ${cardCls}`} data-testid="doctor-soap-recorder">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {bg ? "SOAP бележка с гласово въвеждане" : "Voice-to-SOAP note"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? "Whisper STT → GPT-5.2 → структурирана бележка за редактиране" : "Whisper STT → GPT-5.2 → structured note for review"}
            </p>
          </div>
        </div>
        {history.length > 0 && (
          <button
            onClick={() => setHistoryOpen((o) => !o)}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${
              isDark ? "bg-violet-500/15 text-violet-300 hover:bg-violet-500/25"
                     : "bg-violet-50 text-violet-700 hover:bg-violet-100"
            }`}
            data-testid="soap-history-toggle"
          >
            {bg ? `Минали ${history.length}` : `Past ${history.length}`}
            {historyOpen ? <ChevronUp className="inline w-3.5 h-3.5 ml-1" /> : <ChevronDown className="inline w-3.5 h-3.5 ml-1" />}
          </button>
        )}
      </div>

      {/* History list */}
      {historyOpen && history.length > 0 && (
        <div className={`rounded-xl border ${isDark ? "border-white/5 bg-[hsl(222,47%,7%)]" : "border-gray-100 bg-slate-50"} divide-y ${isDark ? "divide-white/5" : "divide-gray-100"}`}>
          {history.map((n) => (
            <div key={n.id} className="flex items-center justify-between px-3 py-2 gap-2" data-testid={`soap-history-item-${n.id}`}>
              <button onClick={() => loadFromHistory(n)} className="flex-1 text-left">
                <div className={`text-xs font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {n.patient_name || (bg ? "(без име)" : "(no name)")}
                </div>
                <div className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                  {(n.consultation_date || n.created_at || "").slice(0, 16).replace("T", " ")}
                </div>
              </button>
              <button onClick={() => deleteNote(n.id)} className={`p-1.5 rounded-lg ${isDark ? "hover:bg-white/5 text-gray-500" : "hover:bg-gray-200 text-gray-400"}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Patient name input */}
      <input
        type="text"
        placeholder={bg ? "Име на пациент (по желание)" : "Patient name (optional)"}
        value={patientName}
        onChange={(e) => setPatientName(e.target.value)}
        className={`w-full rounded-lg border px-3 py-2 text-sm ${inputCls}`}
        data-testid="soap-patient-name-input"
      />

      {/* Recording controls */}
      <div className="flex items-center justify-center gap-3">
        {!recording ? (
          <button
            onClick={startRecording}
            disabled={processing}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${
              processing
                ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-md hover:shadow-lg hover:scale-[1.02]"
            }`}
            data-testid="soap-record-start"
          >
            <Mic className="w-4 h-4" />
            {bg ? "Започни запис" : "Start recording"}
          </button>
        ) : (
          <button
            onClick={stopRecording}
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-rose-500 text-white shadow-md animate-pulse"
            data-testid="soap-record-stop"
          >
            <Square className="w-4 h-4" />
            {bg ? `Спри (${minSec})` : `Stop (${minSec})`}
          </button>
        )}
        {processing && (
          <div className={`inline-flex items-center gap-2 text-xs ${isDark ? "text-gray-300" : "text-gray-600"}`}>
            <Loader2 className="w-4 h-4 animate-spin" />
            {bg ? "Транскрипция и структуриране..." : "Transcribing and structuring..."}
          </div>
        )}
      </div>

      {/* Raw transcript (collapsed view) */}
      {transcript && (
        <details className={`rounded-xl border p-3 text-xs ${isDark ? "border-white/5 bg-[hsl(222,47%,7%)] text-gray-300" : "border-gray-100 bg-slate-50 text-gray-600"}`}>
          <summary className="cursor-pointer font-semibold">
            {bg ? "Покажи суров транскрипт" : "Show raw transcript"}
          </summary>
          <pre className="whitespace-pre-wrap mt-2" data-testid="soap-raw-transcript">{transcript}</pre>
        </details>
      )}

      {/* SOAP editable fields */}
      {(transcript || soap.subjective || soap.objective || soap.assessment || soap.plan) && (
        <div className="space-y-3">
          {SECTIONS.map((s) => (
            <div key={s.key}>
              <label className={`block text-[11px] font-bold uppercase tracking-wider mb-1 ${isDark ? "text-violet-300" : "text-violet-700"}`}>
                {bg ? s.label_bg : s.label_en}
              </label>
              <textarea
                value={soap[s.key]}
                onChange={(e) => setSoap((prev) => ({ ...prev, [s.key]: e.target.value }))}
                rows={3}
                className={`w-full rounded-lg border px-3 py-2 text-sm resize-y min-h-[72px] ${inputCls}`}
                data-testid={`soap-field-${s.key}`}
              />
            </div>
          ))}

          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              onClick={resetAll}
              className={`inline-flex items-center gap-2 text-xs font-semibold rounded-full px-3 py-1.5 ${
                isDark ? "text-gray-400 hover:bg-white/5" : "text-gray-500 hover:bg-gray-100"
              }`}
              data-testid="soap-reset-btn"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {bg ? "Нова бележка" : "New note"}
            </button>
            <button
              onClick={saveNote}
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-full px-4 py-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-md hover:shadow-lg"
              data-testid="soap-save-btn"
            >
              <Save className="w-4 h-4" />
              {savedNoteId ? (bg ? "Обнови" : "Update") : (bg ? "Запази" : "Save")}
            </button>
          </div>
        </div>
      )}

      {/* Educational disclaimer */}
      <p className={`text-[10px] leading-relaxed ${isDark ? "text-gray-500" : "text-gray-400"}`}>
        {bg
          ? "Образователен инструмент. SOAP е помощник; финалното решение е на лекаря. Аудио файлът не се съхранява след transcription."
          : "Educational tool. SOAP is an aid; final clinical decision is the physician's. Audio file is not stored after transcription."}
      </p>
    </div>
  );
}
